import { useState, useMemo, memo } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Server, Database, Wifi, Globe, RefreshCw, StickyNote, Search } from 'lucide-react';
import { systemAPI, customerAPI } from '../services/api';
import useAuthStore from '../stores/authStore';
import { useDateFormat } from '../hooks/useDateFormat';

const SystemHealth = memo(() => {
  const navigate = useNavigate();
  const [notesSearch, setNotesSearch] = useState('');
  const { user } = useAuthStore();

  const { data: healthData, isLoading: healthLoading, refetch: refetchHealth } = useQuery(
    'systemHealth',
    () => systemAPI.getHealth().then(res => res.data.data),
    {
      refetchInterval: 60000,
      staleTime: 30000,
      refetchOnMount: false
    }
  );

  const { data: customersWithNotes } = useQuery('customersWithNotes', () =>
    customerAPI.getWithNotes().then(res => res.data.data),
    {
      staleTime: 3 * 60 * 1000,
      cacheTime: 10 * 60 * 1000
    }
  );

  const filteredNotes = useMemo(() => {
    if (!customersWithNotes) return [];
    if (!notesSearch.trim()) return customersWithNotes;
    const q = notesSearch.toLowerCase();
    return customersWithNotes.filter(c =>
      c.customerName.toLowerCase().includes(q) ||
      c.remarks.toLowerCase().includes(q)
    );
  }, [customersWithNotes, notesSearch]);

  const services = useMemo(() => [
    {
      name: 'Frontend',
      key: 'frontend',
      icon: Globe,
      status: 'healthy',
      responseTime: 0,
      description: 'React Application'
    },
    {
      name: 'Backend API',
      key: 'backend',
      icon: Server,
      status: healthData?.services?.backend?.status || 'unknown',
      responseTime: healthData?.services?.backend?.responseTime || 0,
      description: 'Node.js Express Server'
    },
    {
      name: 'PostgreSQL',
      key: 'database',
      icon: Database,
      status: healthData?.services?.database?.status || 'unknown',
      responseTime: healthData?.services?.database?.responseTime || 0,
      description: 'Database Server',
      error: healthData?.services?.database?.error
    },
    {
      name: 'CYTA Service',
      key: 'cytaService',
      icon: Wifi,
      status: healthData?.services?.cytaService?.status || 'unknown',
      responseTime: healthData?.services?.cytaService?.responseTime || 0,
      description: 'Python API Service',
      error: healthData?.services?.cytaService?.error
    }
  ], [healthData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500';
      case 'unhealthy':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'healthy':
        return 'Healthy';
      case 'unhealthy':
        return 'Unhealthy';
      default:
        return 'Unknown';
    }
  };

  const handleRefresh = () => {
    refetchHealth();
  };

  const { formatDate } = useDateFormat();
  const formatTimestamp = (timestamp) => {
    try {
      const d = new Date(timestamp);
      const time = d.toLocaleTimeString();
      return `${formatDate(d)}, ${time}`;
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">System Health</h2>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          disabled={healthLoading}
        >
          <RefreshCw className={`w-4 h-4 ${healthLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Service Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {services.map((service) => {
          const Icon = service.icon;
          return (
            <div key={service.key} className="card">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${service.status === 'healthy' ? 'bg-green-100' : service.status === 'unhealthy' ? 'bg-red-100' : 'bg-gray-100'}`}>
                    <Icon className={`w-5 h-5 ${service.status === 'healthy' ? 'text-green-600' : service.status === 'unhealthy' ? 'text-red-600' : 'text-gray-500'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{service.name}</p>
                    <p className="text-xs text-gray-500">{service.description}</p>
                  </div>
                </div>
                <div className={`w-3 h-3 rounded-full ${getStatusColor(service.status)}`} title={getStatusText(service.status)} />
              </div>
              <div className="mt-3 flex items-center justify-between text-sm">
                <span className={`font-medium ${service.status === 'healthy' ? 'text-green-600' : service.status === 'unhealthy' ? 'text-red-600' : 'text-gray-500'}`}>
                  {getStatusText(service.status)}
                </span>
                {service.responseTime > 0 && (
                  <span className="text-gray-500">{service.responseTime}ms</span>
                )}
              </div>
              {service.error && (
                <p className="mt-2 text-xs text-red-500 truncate" title={service.error}>
                  {service.error}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Last Updated */}
      {healthData?.timestamp && (
        <p className="text-xs text-gray-500 text-right">
          Last updated: {formatTimestamp(healthData.timestamp)}
        </p>
      )}

      {/* Customer Notes */}
      {customersWithNotes?.length > 0 && (
        <div className="card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <StickyNote className="text-primary-500" size={20} />
              <h2 className="text-lg md:text-xl font-bold text-gray-900">Additional Notes</h2>
              <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full">
                {filteredNotes.length}
              </span>
            </div>
            <div className="relative sm:ml-4 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search notes..."
                value={notesSearch}
                onChange={(e) => setNotesSearch(e.target.value)}
                className="input pl-9 py-1.5 text-sm"
              />
            </div>
          </div>
          <div className="overflow-auto -mx-4 md:mx-0 max-h-96">
            <table className="w-full min-w-[400px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b">
                  <th className="text-left py-2 md:py-3 px-3 md:px-4 font-medium text-gray-700 text-sm md:text-base w-1/4">Customer</th>
                  <th className="text-left py-2 md:py-3 px-3 md:px-4 font-medium text-gray-700 text-sm md:text-base">Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredNotes.map((customer) => (
                  <tr key={customer.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 md:py-3 px-3 md:px-4">
                      <button
                        className="text-primary-600 hover:text-primary-800 hover:underline font-medium text-sm md:text-base text-left"
                        onClick={() => navigate(`/customers/${customer.id}`)}
                      >
                        {customer.customerName}
                      </button>
                    </td>
                    <td className="py-2 md:py-3 px-3 md:px-4 text-sm md:text-base text-gray-600 whitespace-pre-wrap">
                      {customer.remarks}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
});

export default SystemHealth;
